import { Amplify } from 'aws-amplify';
import { generateClasses } from '@formkit/themes';
import { defaultConfig, plugin } from '@formkit/vue';

import { createAuth } from '@infuse/auth/plugin';
import { configureEntra, configureAzureB2C, PasswordPolicy, DEFAULT_PASSWORD_POLICY } from '@infuse/auth/services';
import { getIamFeatures } from '@infuse/api/iam/feature';
import { getInfuseServices, InfuseServiceType } from '@infuse/api/feature';
import { Feature, useFeatureStore } from '@infuse/cpms/shared/features/store';
import { useCurrentUserStore } from '@infuse/iam/user/store';
import { MainLayout } from '@infuse/ui/layouts';

import { AppRouter } from '@infuse/ui/router';
import axios from 'axios';
import { createPinia } from 'pinia';
import { App, Component, createApp } from 'vue';
import { RouteRecordRaw } from 'vue-router';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';

interface UiCognitoConfig {
  region: string;
  userPoolId: string;
  clientId: string;
  passwordPolicy?: PasswordPolicy;
}

interface UiEntraConfig {
  clientId: string;
  tenantId: string;
  redirectUri: string;
  logoutRedirectUri?: string;
  scopes?: string[];
}

interface UiAzureB2CConfig {
  clientId: string;
  tenantName: string;
  policies: string;
  /** Optional human-readable label per policy, used for the login buttons. */
  policyDisplayNames?: Record<string, string>;
  redirectUri: string;
  logoutRedirectUri?: string;
  scopes?: string;
}

/**
 * Base UI config served by the shared auth endpoint. Product-agnostic. Individual
 * apps that serve extra top-level fields (via the backend api-integration config
 * extension) extend this and pass their type to {@link configureApp}, then read the
 * extra fields from the `uiConfig` callback argument.
 */
export interface UiConfig {
  type: 'basic' | 'cognito' | 'entra' | 'azure-b2c';
  cognito?: UiCognitoConfig;
  entra?: UiEntraConfig;
  azureB2c?: UiAzureB2CConfig;
  iam_app_id?: string;
}

export interface AppConfiguration {
  configUrl: string;
  mainRoute: string;
  enableFeatureDiscovery: boolean;
  enableIamFeatureDiscovery?: boolean;
  /**
   * Route component for the app shell. Defaults to the shared MainLayout. EVE Control
   * passes a wrapper that renders its topbar above MainLayout.
   */
  mainComponent?: Component;
  /** Extra meta merged into the `main` route, e.g. `{ hideSettings: true }`. */
  mainMeta?: RouteRecordRaw['meta'];
}

async function fetchUiConfig<TConfig extends UiConfig = UiConfig>(config: AppConfiguration): Promise<TConfig> {
  return (await axios.get<TConfig>(config.configUrl)).data;
}

export async function configureApp<TConfig extends UiConfig = UiConfig>(
  config: AppConfiguration,
  root: Component,
  callback: (
    app: App,
    features: string[] | undefined,
    iamFeatures: string[],
    services: InfuseServiceType[],
    uiConfig: TConfig
  ) => void
) {
  const res = await fetchUiConfig<TConfig>(config);

  const authType: 'basic' | 'cognito' | 'entra' | 'azure-b2c' = res.type;

  if (authType === 'cognito') {
    if (!res.cognito) {
      throw new Error('Cognito configuration is missing');
    }
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: res.cognito.userPoolId,
          userPoolClientId: res.cognito.clientId,
          passwordFormat: {
            requireLowercase: res.cognito?.passwordPolicy?.requireLowercase ?? DEFAULT_PASSWORD_POLICY.requireLowercase,
            requireUppercase: res.cognito?.passwordPolicy?.requireUppercase ?? DEFAULT_PASSWORD_POLICY.requireUppercase,
            requireNumbers: res.cognito?.passwordPolicy?.requireNumbers ?? DEFAULT_PASSWORD_POLICY.requireNumbers,
            requireSpecialCharacters:
              res.cognito?.passwordPolicy?.requireSymbols ?? DEFAULT_PASSWORD_POLICY.requireSymbols,
            minLength: res.cognito?.passwordPolicy?.minimumLength ?? DEFAULT_PASSWORD_POLICY.minimumLength
          }
        }
      }
    });
  } else if (authType === 'entra') {
    if (!res.entra) {
      throw new Error('Entra configuration is missing');
    }
    // Use scopes from config, fallback to default API scope if not provided
    const scopes = res.entra.scopes || [`api://${res.entra.clientId}/cpms.read`];

    configureEntra({
      clientId: res.entra.clientId,
      tenantId: res.entra.tenantId,
      redirectUri: res.entra.redirectUri,
      logoutRedirectUri: res.entra.logoutRedirectUri ?? res.entra.redirectUri,
      scopes: scopes
    });
  } else if (authType === 'azure-b2c') {
    if (!res.azureB2c) {
      throw new Error('Azure B2C configuration is missing');
    }
    // Parse all policies from the comma-separated list
    const policies = res.azureB2c.policies
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);

    // Parse scopes - can be a comma-separated string or array
    const scopes =
      typeof res.azureB2c.scopes === 'string'
        ? res.azureB2c.scopes
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : res.azureB2c.scopes || [];

    configureAzureB2C({
      clientId: res.azureB2c.clientId,
      tenantName: res.azureB2c.tenantName,
      policies: policies,
      policyDisplayNames: res.azureB2c.policyDisplayNames,
      redirectUri: res.azureB2c.redirectUri,
      logoutRedirectUri: res.azureB2c.logoutRedirectUri ?? res.azureB2c.redirectUri,
      scopes: scopes
    });
  }

  const route: RouteRecordRaw = {
    path: '/',
    name: 'main',
    component: config.mainComponent ?? MainLayout,
    redirect: config.mainRoute,
    meta: {
      ...config.mainMeta,
      authenticated: true
    },
    children: []
  };

  AppRouter.addRoute(route);

  const app = createApp(root);

  const pinia = createPinia();
  pinia.use(piniaPluginPersistedstate);
  app.use(pinia); // first register pinia to make sure we can use it outside vue components

  let features: string[] = [];
  let iamFeatures: string[] = [];
  let services: InfuseServiceType[] = [];

  app.use(
    createAuth({ type: authType }, (authenticator) => {
      (async () => {
        const isAuthenticated = await authenticator.isAuthenticated();

        if (config.enableFeatureDiscovery && isAuthenticated) {
          const featureStore = useFeatureStore();

          await featureStore.getAll();

          features = [...featureStore.items];
        }

        const currentUserStore = useCurrentUserStore();

        if (res.iam_app_id) {
          currentUserStore.setMainSubscriptionId(res.iam_app_id);
        }

        const iamEnabled = features.includes(Feature.IAM) || config.enableIamFeatureDiscovery;

        if (isAuthenticated && iamEnabled) {
          await currentUserStore.fetchCurrentUser();
          currentUserStore.setSentryScope();
          if (config.enableIamFeatureDiscovery) {
            iamFeatures = await getIamFeatures();
          }
        } else if (isAuthenticated) {
          currentUserStore.disable();
        }

        if (isAuthenticated) {
          services = (await getInfuseServices()).map((service) => service.type);
        }

        configureAfterAuth(app, features, iamFeatures, services, res, callback);
      })();
    })
  );
}

export function configureAfterAuth<TConfig extends UiConfig = UiConfig>(
  app: App<Element>,
  features: string[],
  iamFeatures: string[],
  services: InfuseServiceType[],
  uiConfig: TConfig,
  callbackAfterAuth: (
    app: App,
    features: string[],
    iamFeatures: string[],
    services: InfuseServiceType[],
    uiConfig: TConfig
  ) => void
) {
  app.use(
    plugin,
    defaultConfig({
      config: {
        classes: generateClasses({
          submit: {
            outer: 'mt-8',
            input:
              'flex items-center w-fit rounded-md text-xs py-2 px-4 font-bold justify-center bg-secondary-500 text-white hover:bg-secondary-400 active:bg-secondary-600 active:text-white formkit-disabled:bg-primary-50 formkit-disabled:text-neutral-300'
          },
          text: {
            outer: 'mb-4',
            label: 'text-neutral-800 text-sm mb-px',
            inner:
              "flex w-full items-center py-2.5 rounded-md border border-neutral-300 bg-neutral-0 px-4 focus-within:border-secondary-500 focus-within:shadow-[0px_0px_4px_theme('colors.primary.500')] hover:border-secondary-500 active:border-secondary-500 formkit-disabled:border-neutral-400 formkit-disabled:bg-neutral-50",
            prefix: 'mr-3',
            input:
              'w-full text-sm border-none p-0 leading-3 text-neutral-800 outline-none transition-colors placeholder:text-neutral-300 focus:ring-0',
            help: 'text-xs text-neutral-600 mt-1',
            messages: 'list-none p-0 mt-1 mb-0',
            message: 'text-error-500 mb-1 text-xs'
          },
          select: {
            outer: 'mb-4',
            label: 'text-neutral-800 text-sm mb-px',
            inner:
              "flex w-full items-center py-2.5 rounded-md border border-neutral-300 bg-neutral-0 px-4 focus-within:border-secondary-500 focus-within:shadow-[0px_0px_4px_theme('colors.primary.500')] hover:border-secondary-500 active:border-secondary-500 formkit-disabled:border-neutral-400 formkit-disabled:bg-neutral-50",
            prefix: 'mr-3',
            input:
              'w-full text-sm border-none p-0 leading-3 text-neutral-800 outline-none transition-colors placeholder:text-neutral-300 focus:ring-0',
            help: 'text-xs text-neutral-600 mt-1',
            messages: 'list-none p-0 mt-1 mb-0',
            message: 'text-error-500 mb-1 text-xs'
          },
          checkbox: {
            outer: 'mb-4',
            wrapper: 'flex items-center gap-2',
            inner: 'flex items-center',
            label: 'text-neutral-800 text-sm',
            input:
              'h-4 w-4 rounded-sm border-secondary-500 text-primary-500 focus:outline-none focus:ring-0 focus:ring-offset-0 checked:focus:border-[#e0f6e0]'
          }
        })
      }
    })
  );

  callbackAfterAuth(app, features, iamFeatures, services, uiConfig);
  app.use(AppRouter);
  app.mount('#app');
}
